import { FUNCTION_BASE } from "../lib/constants";
import { authFetch } from "../lib/api";
import { useState, useEffect } from "react";
import { auth } from "../lib/firebase";
import { loadStripe } from "@stripe/stripe-js";
import "../styles/Billing.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_YOUR_KEY_HERE");

const statusClass = (status) => {
  const map = {
    active: "billing-badge-active",
    trialing: "billing-badge-trialing",
    past_due: "billing-badge-past-due",
    canceled: "billing-badge-canceled",
    paid: "billing-badge-paid",
    open: "billing-badge-open",
    void: "billing-badge-void",
  };
  return `billing-badge ${map[status] || "billing-badge-open"}`;
};

const BillingPage = () => {
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const user = auth.currentUser;

  useEffect(() => {
    if (user) {
      fetchBillingData();
    }
  }, [user]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const response = await authFetch(
        `https://us-central1-my-saas-a9ed7.cloudfunctions.net/stripe/api/billing/${user.uid}`
      );
      const data = await response.json();
      setBillingData(data);
    } catch (err) {
      setError("Failed to load billing information");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      setLoading(true);
      const response = await authFetch(
        "https://us-central1-my-saas-a9ed7.cloudfunctions.net/stripe/api/create-customer",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: user.email,
            name: user.displayName || "Adsoar User",
          }),
        }
      );
      const data = await response.json();
      await fetchBillingData();
    } catch (err) {
      setError("Failed to create customer");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId) => {
    try {
      setLoading(true);

      let customerId = billingData?.customer?.id;
      if (!customerId) {
        const custResponse = await authFetch(
          "https://us-central1-my-saas-a9ed7.cloudfunctions.net/stripe/api/create-customer",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: user.email,
              name: user.displayName || "Adsoar User",
            }),
          }
        );
        const custData = await custResponse.json();
        customerId = custData.customerId;
      }

      const response = await authFetch(
        "https://us-central1-my-saas-a9ed7.cloudfunctions.net/stripe/api/create-checkout-session",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerId, priceId }),
        }
      );
      const data = await response.json();

      const stripe = await stripePromise;
      await stripe.redirectToCheckout({ sessionId: data.sessionId });
    } catch (err) {
      setError("Failed to start checkout");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setLoading(true);
      const response = await authFetch(
        "https://us-central1-my-saas-a9ed7.cloudfunctions.net/stripe/api/create-portal-session",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerId: billingData.customer.id }),
        }
      );
      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      setError("Failed to open billing portal");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !billingData) {
    return <div className="billing-loading">Loading billing information...</div>;
  }

  const hasActiveSubscription = billingData?.subscriptions?.some(
    (sub) => sub.status === "active" || sub.status === "trialing"
  );

  return (
    <div>
      {/* Header */}
      <div className="billing-header">
        <div>
          <h1 className="billing-title">Billing & Subscription</h1>
          <p className="billing-subtitle">Manage your subscription and billing information</p>
        </div>
      </div>
      {billingData?.customer && (
        <div style={{ marginBottom: 20 }}>
          <button className="billing-portal-btn" onClick={handleManageBilling}>
            Manage Billing
          </button>
        </div>
      )}

      {error && <div className="billing-error">{error}</div>}

      {/* Subscription Status */}
      {hasActiveSubscription ? (
        <div className="billing-card">
          <h2 className="billing-card-title">Current Subscription</h2>
          {billingData.subscriptions.map((sub) => (
            <div key={sub.id} className="billing-sub-card">
              <div className="billing-sub-header">
                <div>
                  <h3 className="billing-sub-name">
                    {sub.items.data[0]?.price?.product?.name || "Adsoar Subscription"}
                  </h3>
                  <p className="billing-sub-price">
                    ${(sub.items.data[0]?.price?.unit_amount / 100).toFixed(2)} / month
                  </p>
                </div>
                <span className={statusClass(sub.status)}>{sub.status}</span>
              </div>
              <div className="billing-sub-info">
                <div className="billing-info-item">
                  <span className="billing-info-label">Current Period:</span>
                  <span className="billing-info-value">
                    {new Date(sub.current_period_start * 1000).toLocaleDateString()} –{" "}
                    {new Date(sub.current_period_end * 1000).toLocaleDateString()}
                  </span>
                </div>
                <div className="billing-info-item">
                  <span className="billing-info-label">Next Billing Date:</span>
                  <span className="billing-info-value">
                    {new Date(sub.current_period_end * 1000).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="billing-card">
          <h2 className="billing-card-title">Choose a Plan</h2>
          <p className="billing-card-subtitle">Select a subscription plan to get started with Adsoar</p>

          <div className="billing-plans-grid">
            {/* Starter */}
            <div className="billing-plan-card">
              <div className="billing-plan-header">
                <h3 className="billing-plan-name">Starter</h3>
                <div className="billing-plan-price">
                  <span className="billing-price-amount">$99</span>
                  <span className="billing-price-period">/month</span>
                </div>
              </div>
              <ul className="billing-feature-list">
                <li className="billing-feature-item">Up to $10,000 ad spend</li>
                <li className="billing-feature-item">Campaign management</li>
                <li className="billing-feature-item">Basic analytics</li>
                <li className="billing-feature-item">Email support</li>
              </ul>
              <button
                className="billing-subscribe-btn"
                onClick={() => handleSubscribe("price_1SoxhoARCQWbZywyOV1xAExb")}
                disabled={loading}
              >
                Subscribe to Starter
              </button>
            </div>

            {/* Professional */}
            <div className="billing-plan-card featured">
              <div className="billing-plan-badge">Most Popular</div>
              <div className="billing-plan-header">
                <h3 className="billing-plan-name">Professional</h3>
                <div className="billing-plan-price">
                  <span className="billing-price-amount">$299</span>
                  <span className="billing-price-period">/month</span>
                </div>
              </div>
              <ul className="billing-feature-list">
                <li className="billing-feature-item">Up to $50,000 ad spend</li>
                <li className="billing-feature-item">Advanced campaign tools</li>
                <li className="billing-feature-item">Advanced analytics</li>
                <li className="billing-feature-item">Priority support</li>
                <li className="billing-feature-item">Custom reporting</li>
              </ul>
              <button
                className="billing-subscribe-btn featured-btn"
                onClick={() => handleSubscribe("price_1SoxhpARCQWbZywyU2qiPf7o")}
                disabled={loading}
              >
                Subscribe to Professional
              </button>
            </div>

            {/* Enterprise */}
            <div className="billing-plan-card">
              <div className="billing-plan-header">
                <h3 className="billing-plan-name">Enterprise</h3>
                <div className="billing-plan-price">
                  <span className="billing-price-amount">$999</span>
                  <span className="billing-price-period">/month</span>
                </div>
              </div>
              <ul className="billing-feature-list">
                <li className="billing-feature-item">Unlimited ad spend</li>
                <li className="billing-feature-item">White-label options</li>
                <li className="billing-feature-item">Dedicated account manager</li>
                <li className="billing-feature-item">24/7 phone support</li>
                <li className="billing-feature-item">Custom integrations</li>
              </ul>
              <button
                className="billing-subscribe-btn outline-btn"
                onClick={() => handleSubscribe("price_1SoxhpARCQWbZywyOfI4DRmE")}
                disabled={loading}
              >
                Subscribe to Enterprise
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice History */}
      {billingData?.invoices && billingData.invoices.length > 0 && (
        <div className="billing-card">
          <h2 className="billing-card-title">Invoice History</h2>
          <div className="billing-table-scroll">
            <table className="billing-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {billingData.invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{new Date(invoice.created * 1000).toLocaleDateString()}</td>
                    <td>{invoice.lines.data[0]?.description || "Subscription"}</td>
                    <td>${(invoice.amount_paid / 100).toFixed(2)}</td>
                    <td>
                      <span className={statusClass(invoice.status)}>{invoice.status}</span>
                    </td>
                    <td>
                      {invoice.invoice_pdf && (
                        <a
                          href={invoice.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="billing-download-link"
                        >
                          Download
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
